import { Component, inject, isDevMode, OnDestroy, signal } from "@angular/core";
import { MatIconRegistry } from "@angular/material/icon";
import { DomSanitizer } from "@angular/platform-browser";
import {
  NavigationCancel,
  NavigationEnd,
  NavigationError,
  NavigationStart,
  Router,
} from "@angular/router";
import { NGXLogger } from "ngx-logger";
import { Subscription } from "rxjs";
import { AppScreenMode, AppStore } from "./core/stores/app.store";
import { LifeCycleStore } from "./core/stores/lifecycle.store";
import { LogStore } from "./core/stores/log.store";
import { VersionCheckService } from "./shared/services/version-check.service";

declare let gtag: Function;

@Component({
  selector: "app-root",
  templateUrl: "./app.component.html",
  styleUrls: ["./app.component.scss"],
})
export class AppComponent implements OnDestroy {
  private _lifecycleStore: LifeCycleStore = inject(LifeCycleStore);
  private _appStore: AppStore = inject(AppStore);
  private _subRouter: Subscription;
  private _versionCheckService: VersionCheckService =
    inject(VersionCheckService);
  private logStore: LogStore = inject(LogStore);
  private _logger: NGXLogger = inject(NGXLogger);
  private router: Router = inject(Router);
  private domSanitizer: DomSanitizer = inject(DomSanitizer);
  private matIconRegistry: MatIconRegistry = inject(MatIconRegistry);

  protected isLoading = signal<boolean>(null);

  constructor() {
    this._logger.registerMonitor(this.logStore);

    if (!isDevMode()) {
      this._versionCheckService.initVersionCheck();
    }

    if (this._subRouter) this._subRouter.unsubscribe();
    this._subRouter = this.router.events.subscribe((event) => {
      if (event instanceof NavigationStart) {
        this.isLoading.set(true);
      }
      if (
        event instanceof NavigationEnd ||
        event instanceof NavigationCancel ||
        event instanceof NavigationError
      ) {
        if (this._appStore.screenMode !== AppScreenMode.Loading) {
          this.isLoading.set(false);
        }
      }

      if (event instanceof NavigationEnd) {
        gtag("config", "UA-51707725-4", {
          page_path: event.urlAfterRedirects,
          cookie_flags: "SameSite=None;Secure",
        });
      }
    });

    this._logger.info("[APP] ================== App loaded ==================");

    this.matIconRegistry.addSvgIcon(
      "flag_french",
      this.domSanitizer.bypassSecurityTrustResourceUrl("assets/flags/fr.svg")
    );
    this.matIconRegistry.addSvgIcon(
      "flag_usa",
      this.domSanitizer.bypassSecurityTrustResourceUrl("assets/flags/us.svg")
    );

    this.matIconRegistry.addSvgIcon(
      "cereus",
      this.domSanitizer.bypassSecurityTrustResourceUrl("assets/cereus.svg")
    );

    this.matIconRegistry.addSvgIcon(
      "aloe",
      this.domSanitizer.bypassSecurityTrustResourceUrl("assets/aloe.svg")
    );

    this.matIconRegistry.addSvgIcon(
      "yucca",
      this.domSanitizer.bypassSecurityTrustResourceUrl("assets/yucca.svg")
    );

    this.matIconRegistry.addSvgIcon(
      "first",
      this.domSanitizer.bypassSecurityTrustResourceUrl("assets/first.svg")
    );
    this.matIconRegistry.addSvgIcon(
      "meter",
      this.domSanitizer.bypassSecurityTrustResourceUrl("assets/meter.svg")
    );

    this.matIconRegistry.addSvgIcon(
      "chrono",
      this.domSanitizer.bypassSecurityTrustResourceUrl("assets/chrono.svg")
    );
    this.matIconRegistry.addSvgIcon(
      "drop",
      this.domSanitizer.bypassSecurityTrustResourceUrl("assets/drop_fat.svg")
    );
    this.matIconRegistry.addSvgIcon(
      "shower",
      this.domSanitizer.bypassSecurityTrustResourceUrl("assets/shower.svg")
    );
    this.matIconRegistry.addSvgIcon(
      "water_usage",
      this.domSanitizer.bypassSecurityTrustResourceUrl("assets/water_usage.svg")
    );

    this.matIconRegistry.addSvgIcon(
      "shower_head",
      this.domSanitizer.bypassSecurityTrustResourceUrl("assets/shower_head.svg")
    );

    this.matIconRegistry.addSvgIcon(
      "account_authorization_pending",
      this.domSanitizer.bypassSecurityTrustResourceUrl(
        "assets/account/account_authorization_pending.svg"
      )
    );

    this.matIconRegistry.addSvgIcon(
      "account_active",
      this.domSanitizer.bypassSecurityTrustResourceUrl(
        "assets/account/account_active.svg"
      )
    );

    this.matIconRegistry.addSvgIcon(
      "account_authorization_refused",
      this.domSanitizer.bypassSecurityTrustResourceUrl(
        "assets/account/account_authorization_refused.svg"
      )
    );

    this.matIconRegistry.addSvgIcon(
      "account_creation_pending",
      this.domSanitizer.bypassSecurityTrustResourceUrl(
        "assets/account/account_creation_pending.svg"
      )
    );

    this.matIconRegistry.addSvgIcon(
      "lora",
      this.domSanitizer.bypassSecurityTrustResourceUrl("assets/lora.svg")
    );

    this.matIconRegistry.addSvgIcon(
      "smart_alert",
      this.domSanitizer.bypassSecurityTrustResourceUrl("assets/smart_alert.svg")
    );

    this.matIconRegistry.addSvgIcon(
      "threshold_alert",
      this.domSanitizer.bypassSecurityTrustResourceUrl(
        "assets/threshold_alert.svg"
      )
    );

    this.matIconRegistry.addSvgIcon(
      "hydrao_connect",
      this.domSanitizer.bypassSecurityTrustResourceUrl(
        "assets/hydrao-connect.svg"
      )
    );
  }

  ngOnDestroy(): void {
    if (this._subRouter) this._subRouter.unsubscribe();
  }
}
